'use client';
import { useAnalyticsPageView } from '../components/PageView/Context';
import { getCleanLocationHref } from '../lib/urlUtils';
import { useAnalytics } from './useAnalytics';

type PageActionProps = {
  actionInput?: string | null;
  actionName?: string | null;
  actionSection?: string;
  actionType?: string;
  activityId?: string;
  destinationURL?: string;
  impressionSection?: string;
  impressionPosition?: number;
  parentActivityId?: string;
  targetPageURL?: string;
  additionalAttributes?: object;
};

export const usePageAction = ({
  actionInput,
  actionName,
  actionSection,
  actionType,
  activityId,
  destinationURL,
  impressionSection,
  impressionPosition,
  parentActivityId,
  targetPageURL,
  additionalAttributes,
}: PageActionProps) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const { pageViewId, pageName } = useAnalyticsPageView();

  const isClient = typeof window !== 'undefined';

  const segmentProps = {
    action_input: actionInput,
    action_name: actionName,
    action_section: actionSection,
    action_type: actionType,
    activity_id: activityId,
    destination_url: destinationURL,
    parent_activity_id: parentActivityId,
    target_page_url: targetPageURL,
    deployable_name: deployableName,
    deployable_version: deployableVersion,
    impression_page_name: pageName || (isClient && window.location.pathname),
    impression_pageview_id: pageViewId,
    impression_section: impressionSection,
    impression_position: impressionPosition,
    traditional_device_id: deviceId,
    additional_attributes: additionalAttributes,
  };

  return (
    overrideProps?: Record<string, string | object | number | null> | null,
    callback?: () => void,
  ) =>
    analytics &&
    void analytics
      .track(
        'Page Action',
        {
          ...segmentProps,
          // Read live at fire-time (not hook-render-time) so these always reflect
          // the page the user is actually on when the action fires.
          action_location_url: isClient ? window.location.href : undefined,
          impression_page_url: isClient ? getCleanLocationHref() : undefined,
          ...overrideProps,
        },
        { context: { app: { name: deployableName, version: gitSha } } },
      )
      ?.finally(() => {
        callback?.();
      });
};

export type PageActionFireFn = ReturnType<typeof usePageAction>;
